import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { Alert, Box, Typography } from '@mui/material';
import React from 'react';
import { labelColors } from '../theme';
import { Granularity, Label, LabelType } from '../types';

interface EssayContentProps {
  fragments: string[];
  selectedFragmentIndex: number | null;
  isTranscriptionUnidentifiable?: boolean;
  handleFragmentClick: (fragment: string, index: number) => void;
  getLabelForFragment?: (index: number) => Label | null;
  getLabelTypeForFragment?: (index: number) => LabelType | null;
  isFragmentReviewed?: (index: number) => boolean;
  granularity?: Granularity;
  emphasizeSelected?: boolean;
}

const getStylesForLabelType = (
  labelType: LabelType | null,
  isTranscriptionUnidentifiable: boolean
) => {
  let bgColor = 'transparent';
  let borderColor = '1px solid transparent';
  let hoverBgColor = isTranscriptionUnidentifiable ? 'transparent' : 'rgba(0, 0, 0, 0.05)';

  if (labelType) {
    const colors = labelColors[labelType];
    if (colors) {
      bgColor = colors.main;
      borderColor = `1px solid ${colors.border}`;
      hoverBgColor = isTranscriptionUnidentifiable ? bgColor : colors.hover;
    }
  }

  return { bgColor, borderColor, hoverBgColor };
};

const EssayContent: React.FC<EssayContentProps> = ({
  fragments,
  selectedFragmentIndex,
  isTranscriptionUnidentifiable = false,
  handleFragmentClick,
  getLabelForFragment,
  getLabelTypeForFragment,
  isFragmentReviewed,
  granularity = 'sentence',
  emphasizeSelected = false,
}) => {
  return (
    <>
      {isTranscriptionUnidentifiable && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Transcription has been marked as unidentifiable for this policy.
        </Alert>
      )}

      <Box data-testid="transcript-scroll" sx={{ flex: 1, overflow: 'auto', minHeight: 0 }}>
        {fragments.map((fragment, index) => {
          const label = getLabelForFragment ? getLabelForFragment(index) : null;
          const labelType = getLabelTypeForFragment
            ? getLabelTypeForFragment(index)
            : (label?.type ?? null);

          const hasParagraphBreak = fragment.startsWith('\n\n');
          const displayFragment = hasParagraphBreak ? fragment.substring(2) : fragment;

          let { bgColor, borderColor, hoverBgColor } = getStylesForLabelType(
            labelType,
            isTranscriptionUnidentifiable
          );

          const isSelected = selectedFragmentIndex === index;
          if (isSelected) {
            bgColor = emphasizeSelected ? '#c8ebe6' : 'rgba(25, 118, 210, 0.1)';
            hoverBgColor = isTranscriptionUnidentifiable
              ? bgColor
              : emphasizeSelected
                ? '#b2dfdb'
                : 'rgba(25, 118, 210, 0.2)';
          }

          const isBlockLevel = granularity === 'paragraph' || granularity === 'essay';
          const reviewed = isFragmentReviewed ? isFragmentReviewed(index) : false;

          return (
            <React.Fragment key={index}>
              {hasParagraphBreak && granularity === 'sentence' && (
                <Box
                  data-testid={`paragraph-break-${index}`}
                  sx={{ height: '1em', width: '100%' }}
                />
              )}
              <Typography
                component={isBlockLevel ? 'div' : 'span'}
                data-testid={`fragment-${index}`}
                sx={{
                  px: isBlockLevel ? 1.5 : 0,
                  py: isBlockLevel ? 1 : 0,
                  borderRadius: isBlockLevel ? 1 : 0,
                  cursor: isTranscriptionUnidentifiable ? 'not-allowed' : 'pointer',
                  backgroundColor: bgColor,
                  '&:hover': {
                    backgroundColor: hoverBgColor,
                  },
                  opacity: isTranscriptionUnidentifiable ? 0.7 : 1,
                  display: isBlockLevel ? 'block' : 'inline',
                  mb: isBlockLevel ? 1.5 : 0.5,
                  mr: isBlockLevel ? 0 : 0.5,
                  fontWeight: isSelected && !emphasizeSelected ? 'bold' : 'normal',
                  border: borderColor,
                  whiteSpace:
                    granularity === 'paragraph' || granularity === 'essay' ? 'pre-wrap' : undefined,
                }}
                onClick={() => handleFragmentClick(fragment, index)}
              >
                {reviewed && (
                  <CheckCircleIcon
                    fontSize="inherit"
                    sx={{
                      fontSize: '0.85em',
                      mr: 0.25,
                      verticalAlign: 'text-bottom',
                      color: 'success.main',
                    }}
                    titleAccess="Reviewed"
                  />
                )}
                {displayFragment}
              </Typography>
            </React.Fragment>
          );
        })}
      </Box>
    </>
  );
};

export default EssayContent;
